import { useState, useCallback, useMemo } from "react";

export interface QuestionStatus {
  answered: boolean;
  correct?: boolean;
  flagged: boolean;
  bookmarked: boolean;
  timeSpent?: number;
  selectedIndex?: number;
}

export interface UseQuestionStatusReturn {
  questionStatuses: QuestionStatus[];
  setQuestionStatus: (index: number, status: Partial<QuestionStatus>) => void;
  resetQuestionStatus: (index?: number) => void;
  toggleFlag: (index: number) => void;
  markAnswered: (index: number, selectedIndex: number, correct: boolean) => void;
  updateBookmark: (index: number, bookmarked: boolean) => void;
  getQuestionStatus: (index: number) => QuestionStatus;
  clearAnswer: (index: number) => void;
  stats: {
    answered: number;
    correct: number;
    flagged: number;
    bookmarked: number;
    total: number;
  };
}

export const useQuestionStatus = (totalQuestions: number): UseQuestionStatusReturn => {
  const [questionStatuses, setQuestionStatuses] = useState<QuestionStatus[]>(() =>
    Array.from({ length: totalQuestions }, () => ({
      answered: false,
      flagged: false,
      bookmarked: false,
      timeSpent: 0
    }))
  );

  const setQuestionStatus = useCallback((index: number, status: Partial<QuestionStatus>) => {
    setQuestionStatuses(prev => {
      const newStatuses = [...prev];
      newStatuses[index] = { ...newStatuses[index], ...status };
      return newStatuses;
    });
  }, []);

  const resetQuestionStatus = useCallback((index?: number) => {
    if (index !== undefined) {
      setQuestionStatus(index, {
        answered: false,
        correct: undefined,
        selectedIndex: undefined,
        timeSpent: 0
      });
    } else {
      setQuestionStatuses(
        Array.from({ length: totalQuestions }, () => ({
          answered: false,
          flagged: false,
          bookmarked: false,
          timeSpent: 0
        }))
      );
    }
  }, [totalQuestions, setQuestionStatus]);

  const toggleFlag = useCallback((index: number) => {
    setQuestionStatuses(prev => {
      const newStatuses = [...prev];
      newStatuses[index] = {
        ...newStatuses[index],
        flagged: !newStatuses[index].flagged
      };
      return newStatuses;
    });
  }, []);

  const markAnswered = useCallback((index: number, selectedIndex: number, correct: boolean) => {
    setQuestionStatus(index, {
      answered: true,
      correct,
      selectedIndex
    });
  }, [setQuestionStatus]);

  const updateBookmark = useCallback((index: number, bookmarked: boolean) => {
    setQuestionStatus(index, { bookmarked });
  }, [setQuestionStatus]);

  const getQuestionStatus = useCallback((index: number): QuestionStatus => {
    return questionStatuses[index] || {
      answered: false,
      flagged: false,
      bookmarked: false,
      timeSpent: 0
    };
  }, [questionStatuses]);

  const clearAnswer = useCallback((index: number) => {
    setQuestionStatus(index, {
      answered: false,
      correct: undefined,
      selectedIndex: undefined,
      // Keep timeSpent, flagged, and bookmarked status
    });
  }, [setQuestionStatus]);

  const stats = useMemo(() => {
    const answered = questionStatuses.filter(q => q.answered).length;
    const correct = questionStatuses.filter(q => q.correct === true).length;
    const flagged = questionStatuses.filter(q => q.flagged).length;
    const bookmarked = questionStatuses.filter(q => q.bookmarked).length;

    return {
      answered,
      correct,
      flagged,
      bookmarked,
      total: totalQuestions
    };
  }, [questionStatuses, totalQuestions]);

  return {
    questionStatuses,
    setQuestionStatus,
    resetQuestionStatus,
    toggleFlag,
    markAnswered,
    updateBookmark,
    getQuestionStatus,
    clearAnswer,
    stats
  };
};